import { AlertTriangle, Wrench, ShieldAlert } from 'lucide-react';
import type { Equipment } from '@/types/equipment';
import {
    isAtParLevel,
    isMaintenanceOverdue,
    isMaintenanceDueSoon,
    isInspectionOverdue,
    isInspectionDueSoon,
    daysUntilMaintenance
} from '@/types/equipment';

// ============================================================================
// EquipmentAlerts
// ----------------------------------------------------------------------------
// Compact badges shown in the list view for each row. Indicates conditions
// that need a materials manager's attention:
//
//   - Par level: on_hand <= min  →  red  (replenish)
//   - Maint overdue: next_maintenance_due in past  →  red
//   - Maint due soon: <= 7 days  →  amber
//   - Inspection overdue  →  red (regulatory risk)
//   - Inspection due soon (<=14 days)  →  amber
//
// Multiple conditions can fire simultaneously; we render up to 3 to keep
// rows from getting unwieldy. The count is shown if more than 3 fire.
// ============================================================================

interface Alert {
    icon: typeof AlertTriangle;
    label: string;
    severity: 'critical' | 'warning';
    title: string;
}

function buildAlerts(eq: Equipment): Alert[] {
    const alerts: Alert[] = [];

    if (isAtParLevel(eq)) {
        alerts.push({
            icon: AlertTriangle,
            label: `${eq.quantity_on_hand}/${eq.minimum_quantity}`,
            severity: 'critical',
            title: `At or below par level (on hand ${eq.quantity_on_hand}, min ${eq.minimum_quantity})`
        });
    }

    if (isMaintenanceOverdue(eq)) {
        const days = Math.abs(daysUntilMaintenance(eq) ?? 0);
        alerts.push({
            icon: Wrench,
            label: `${days}d`,
            severity: 'critical',
            title: `Maintenance overdue by ${days} day${days === 1 ? '' : 's'}`
        });
    } else if (isMaintenanceDueSoon(eq)) {
        const days = daysUntilMaintenance(eq) ?? 0;
        alerts.push({
            icon: Wrench,
            label: `${days}d`,
            severity: 'warning',
            title: `Maintenance due in ${days} day${days === 1 ? '' : 's'}`
        });
    }

    if (isInspectionOverdue(eq)) {
        alerts.push({
            icon: ShieldAlert,
            label: 'Insp',
            severity: 'critical',
            title: `Inspection overdue (was due ${eq.inspection_due_date})`
        });
    } else if (isInspectionDueSoon(eq)) {
        alerts.push({
            icon: ShieldAlert,
            label: 'Insp',
            severity: 'warning',
            title: `Inspection due soon (due ${eq.inspection_due_date})`
        });
    }

    return alerts;
}

export default function EquipmentAlerts({ equipment }: { equipment: Equipment }) {
    const alerts = buildAlerts(equipment);

    if (alerts.length === 0) {
        return <span className="text-pt-text-dim text-xs">—</span>;
    }

    const visible = alerts.slice(0, 3);
    const overflow = alerts.length - visible.length;

    return (
        <div className="flex flex-wrap items-center gap-1">
            {visible.map((a, i) => {
                const Icon = a.icon;
                const colors =
                    a.severity === 'critical'
                        ? 'bg-pt-red/10 text-pt-red border-pt-red/30'
                        : 'bg-pt-amber/10 text-pt-amber border-pt-amber/30';
                return (
                    <span
                        key={i}
                        title={a.title}
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded border ${colors}`}
                    >
                        <Icon size={10} strokeWidth={2.5} />
                        {a.label}
                    </span>
                );
            })}
            {overflow > 0 && (
                <span className="text-[10px] text-pt-text-dim">+{overflow}</span>
            )}
        </div>
    );
}

// Export the helper so the dashboard can show summary counts
export function countEquipmentAlerts(rows: Equipment[]): {
    par: number;
    maintenance: number;
    inspection: number;
    total: number;
} {
    let par = 0;
    let maintenance = 0;
    let inspection = 0;
    for (const r of rows) {
        if (isAtParLevel(r)) par++;
        if (isMaintenanceOverdue(r)) maintenance++;
        if (isInspectionOverdue(r)) inspection++;
    }
    return { par, maintenance, inspection, total: par + maintenance + inspection };
}
